/**
 * Trip CO₂ Calculator — "Can't take trains?" plan-B dialog.
 *
 * Two pieces:
 *   1. A small button that says "Can't take trains?" — sits under the
 *      chart's methodology panel.
 *   2. A native `<dialog>` modal containing the plan-B pitch + an
 *      embedded `SignupCTAButton` (source: 'travel_impact_calculator_cant_train_dialog').
 *
 * We use the native `<dialog>` element rather than a custom modal:
 *   • Focus trap, Esc-to-close, and the `::backdrop` pseudo-element
 *     come for free.
 *   • No CSS framework or aria-modal scaffolding to maintain.
 *   • 95%+ browser support per PRD §8.
 *
 * Phase 6 of the Trip CO₂ Calculator (PRD §5.8, §6.8).
 */

import React, { useEffect, useRef } from 'react';
import SignupCTAButton from './SignupCTAButton';

export interface CantTrainDialogProps {
  /**
   * Hook for the parent to instrument open events (PRD §9 —
   * `travel_impact_calculator_cant_train_open` is a frontend-tracked event).
   * Optional so the component is usable without analytics wired.
   */
  onOpen?: () => void;
  /**
   * Forwarded to the embedded `SignupCTAButton` so the
   * `travel_impact_calculator_signup_click` Mixpanel event picks
   * up the trip context (origin, destination, mode, roundTrip,
   * co2eKg, distanceKm). Same shape as the one passed to
   * `MeetJodyCTA` — the page builds it once.
   */
  trackingProps?: Record<string, unknown>;
}

const CantTrainDialog: React.FC<CantTrainDialogProps> = ({ onOpen, trackingProps }) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // Close on Escape comes for free with showModal(); we just need to
  // make sure clicking the backdrop also closes (default in some
  // browsers, polyfilled below for the rest).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClick = (e: MouseEvent) => {
      // Clicks on the <dialog> element itself (i.e. backdrop) — not
      // on its child content — should close. Determined via target rect.
      if (e.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) dialog.close();
    };
    dialog.addEventListener('click', onClick);
    return () => dialog.removeEventListener('click', onClick);
  }, []);

  const open = () => {
    onOpen?.();
    dialogRef.current?.showModal();
  };
  const close = () => dialogRef.current?.close();

  return (
    <>
      {/* CTA card (replaces the old pill button). Designed as an
          inviting stepping-stone for users for whom flying is the
          only realistic option — that audience is one of the highest-
          intent conversion targets for signup. Subdued mint
          palette so it doesn't compete with the Meet Jody CTA at the
          bottom of the page; arrow affordance + whole-card click. */}
      <button
        type="button"
        onClick={open}
        aria-label="Can't take trains for this trip? Read about Jody's plan B."
        style={{
          // Radius + marginTop kept in sync with the other three
          // sections above (ComparisonLeadIn, ResultChart,
          // PerspectiveSection) so the four-card stack is visually
          // cohesive. The green-tinted surface stays distinct on
          // purpose — this card is the CTA in the stack.
          marginTop: '2rem',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, hsl(134, 55%, 96%), hsl(147, 72%, 94%))',
          border: '1px solid hsl(134, 55%, 70%)',
          borderRadius: 10,
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(4, 120, 87, 0.18)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <span
          aria-hidden
          style={{
            flex: '0 0 auto',
            fontSize: 28,
            lineHeight: 1,
          }}
        >
          🌿
        </span>
        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 700,
              color: 'hsl(147, 72%, 22%)',
              lineHeight: 1.3,
              marginBottom: 2,
            }}
          >
            Can't take trains for this trip?
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 13,
              color: 'hsl(147, 72%, 30%)',
              lineHeight: 1.4,
            }}
          >
            No problem. Jody finds the smartest mix when flying is on the menu, balancing
            carbon, time, and budget.
          </span>
        </span>
        <span
          aria-hidden
          style={{
            flex: '0 0 auto',
            fontSize: 20,
            fontWeight: 800,
            color: 'hsl(147, 72%, 30%)',
            marginLeft: 4,
          }}
        >
          →
        </span>
      </button>

      <dialog
        ref={dialogRef}
        aria-labelledby="cant-train-title"
        style={{
          padding: 0,
          border: 0,
          borderRadius: 14,
          width: 'min(420px, 92vw)',
          background: '#fff',
          color: '#0f172a',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)',
        }}
      >
        <div style={{ padding: '1.25rem 1.25rem 1.1rem', position: 'relative' }}>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              padding: '4px 8px',
              fontSize: 16,
              lineHeight: 1,
              background: 'transparent',
              border: 0,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: 'hsl(147, 72%, 25%)',
              background: 'hsl(134, 55%, 94%)',
              border: '1px solid hsl(134, 55%, 72%)',
              borderRadius: 999,
              marginBottom: 10,
            }}
          >
            🌿 Plan B
          </div>

          <h3
            id="cant-train-title"
            style={{ fontSize: 20, margin: '0 0 0.6rem', letterSpacing: -0.3, lineHeight: 1.2 }}
          >
            Trains aren't always the answer.
          </h3>

          <p style={{ margin: '0 0 1rem', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
            Sometimes the train is too long, too pricey, or just doesn't run where you're
            going. Jody finds the smartest compromise, balancing carbon, time, and budget,
            even when flying is part of it.
          </p>

          <div
            style={{
              padding: '0.9rem',
              background: 'linear-gradient(135deg, hsl(145, 71%, 23%), hsl(147, 72%, 37%))',
              borderRadius: 10,
            }}
          >
            <SignupCTAButton
              source="travel_impact_calculator_cant_train_dialog"
              trackingProps={trackingProps}
            />
          </div>

          <p
            style={{
              margin: '0.7rem 0 0',
              fontSize: 11,
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            Free · No credit card needed
          </p>
        </div>
      </dialog>
    </>
  );
};

export default CantTrainDialog;
